import React, { useEffect, useState } from "react";
import { api } from "../../lib/api";
import { formatSgd, formatNum, apiErr } from "../../lib/utils";
import { Badge } from "../ui/badge";
import { toast } from "sonner";
import { Target, Link2 } from "lucide-react";
import { STAGE_TONE } from "./InvestmentRegister";

function GapBar({ t }) {
  const total = Math.max(t.required_remaining, t.approved_contribution + t.pipeline_contribution, 1);
  const seg = (v) => `${Math.min((v / total) * 100, 100)}%`;
  return (
    <div>
      <div className="flex h-3 rounded-sm overflow-hidden border border-border" data-testid={`sf-gap-bar-${t.id}`}>
        {t.approved_contribution > 0 && <div style={{ width: seg(t.approved_contribution), background: "#00E65B", opacity: 0.8 }} title="Approved projects" />}
        {t.pipeline_contribution > 0 && <div style={{ width: seg(t.pipeline_contribution), background: "#FFB800", opacity: 0.8 }} title="Pipeline projects" />}
        {t.residual_gap > 0 && <div style={{ width: seg(t.residual_gap), background: "#f87171", opacity: 0.45 }} title="Remaining gap" />}
      </div>
      <div className="flex flex-wrap gap-x-4 gap-y-1 mt-2 text-[10px] text-muted-foreground">
        <span className="inline-flex items-center gap-1"><span className="h-2 w-2 rounded-sm inline-block bg-[#00E65B]" /> Approved <span className="font-mono">{formatNum(t.approved_contribution, { decimals: 1 })}</span></span>
        <span className="inline-flex items-center gap-1"><span className="h-2 w-2 rounded-sm inline-block bg-[#FFB800]" /> Pipeline <span className="font-mono">{formatNum(t.pipeline_contribution, { decimals: 1 })}</span></span>
        <span className="inline-flex items-center gap-1"><span className="h-2 w-2 rounded-sm inline-block bg-red-400/60" /> Remaining gap <span className="font-mono">{formatNum(t.residual_gap, { decimals: 1 })}</span></span>
        {t.delivered_actual > 0 && <span className="text-[#00E65B]">✓ {formatNum(t.delivered_actual, { decimals: 1 })} {t.unit} delivered (MRV actuals)</span>}
      </div>
    </div>
  );
}

export default function TargetGapDashboard() {
  const [d, setD] = useState(null);
  useEffect(() => { api.get("/susfin/target-gaps").then((r) => setD(r.data)).catch((e) => toast.error(apiErr(e))); }, []);
  if (!d) return <div className="p-8 text-muted-foreground font-mono text-sm">Loading target funding gaps…</div>;

  if ((d.targets || []).length === 0) return (
    <div className="card-flat p-12 text-center text-muted-foreground" data-testid="sf-gap-empty">
      <Target className="h-8 w-8 mx-auto mb-3 opacity-50" />
      No sustainability targets set yet — create targets in the Environment module to run a transition finance gap analysis.
    </div>
  );

  return (
    <div className="space-y-4" data-testid="sf-gap-dashboard">
      <p className="text-sm text-muted-foreground">
        Transition Finance Gap Analysis — for every target: expected improvement from linked investments, the residual gap, and the capital required to close it.
      </p>
      {d.targets.map((t) => (
        <div key={t.id} className="card-flat p-5 space-y-4" data-testid={`sf-gap-target-${t.id}`}>
          <div className="flex items-start justify-between flex-wrap gap-2">
            <div>
              <div className="font-display font-semibold text-lg flex items-center gap-2 flex-wrap">
                <Target className="h-4 w-4 text-accent" /> {t.name}
                <Badge variant="outline" className="text-[9px] uppercase">FY{t.target_year}</Badge>
                <Badge variant="outline" className="text-[9px]">{t.years_left} yrs left</Badge>
              </div>
              <div className="text-xs text-muted-foreground mt-0.5">
                {t.metric} · baseline {formatNum(t.baseline_value)} ({t.baseline_year}) → target {formatNum(t.target_value)}
                {t.current_value != null ? ` · current ${formatNum(t.current_value)}` : ""} {t.unit}
              </div>
            </div>
            <div className="text-right">
              <div className="text-[10px] uppercase tracking-wider text-muted-foreground">Required reduction remaining</div>
              <div className="font-mono text-2xl font-bold" data-testid={`sf-gap-required-${t.id}`}>{formatNum(t.required_remaining, { decimals: 1 })} <span className="text-xs text-muted-foreground">{t.unit}</span></div>
            </div>
          </div>

          <GapBar t={t} />

          <div className="grid grid-cols-2 lg:grid-cols-4 gap-3">
            {[["CAPEX required", t.capex_required], ["Committed CAPEX", t.committed_capex],
              ["Potential external funding", t.potential_funding], ["Capital gap", t.capital_gap]].map(([l, v], idx) => (
              <div key={l} className={`border rounded-sm p-3 ${idx === 3 && v > 0 ? "border-red-400/40" : "border-border"}`} data-testid={`sf-gap-cap-${t.id}-${idx}`}>
                <div className="text-[10px] uppercase tracking-wider text-muted-foreground">{l}</div>
                <div className={`font-mono text-lg font-bold ${idx === 3 && v > 0 ? "text-red-400" : ""}`}>{formatSgd(v)}</div>
              </div>
            ))}
          </div>

          {t.linked.length > 0 ? (
            <div>
              <div className="label-eyebrow text-[10px] mb-2">Linked investments</div>
              <div className="space-y-1">
                {t.linked.map((i) => (
                  <div key={i.id} className="flex items-center gap-3 text-xs border border-border/60 rounded-sm px-3 py-2" data-testid={`sf-gap-linked-${i.id}`}>
                    <span className="flex-1 min-w-0 truncate font-medium">{i.name}</span>
                    <Badge variant="outline" className={`text-[8px] uppercase shrink-0 ${STAGE_TONE[i.stage] || ""}`}>{i.stage_label}</Badge>
                    <span className="font-mono w-24 text-right shrink-0">{i.contribution ? `${formatNum(i.contribution, { decimals: 1 })} ${t.unit}` : "—"}</span>
                    <span className="font-mono w-24 text-right text-muted-foreground shrink-0">{i.capex ? formatSgd(i.capex) : "—"}</span>
                  </div>
                ))}
              </div>
            </div>
          ) : (
            <p className="text-xs text-muted-foreground inline-flex items-center gap-1" data-testid={`sf-gap-nolinked-${t.id}`}>
              <Link2 className="h-3.5 w-3.5" /> No investments linked to this target yet — link them from the register (ESG &amp; Climate tab).
            </p>
          )}
        </div>
      ))}
      <p className="text-[10px] text-muted-foreground">{d.disclaimer}</p>
    </div>
  );
}
